<script setup lang="ts">
    import { useAiAssistantPage } from '~/composables/useAiAssistantPage';
    import { useAiAssistantStore, AI_ASSISTANT_MODAL_NAME, AI_ASSISTANT_QUERY_PARAM } from '~/stores/aiAssistantStore';
    import { useEvent, useListen } from '~/composables/useEventBus';

    const router = useRouter();
    const route = useRoute();
    const store = useAiAssistantStore();
    const page = useAiAssistantPage();

    const opened = ref(false);

    const queryValue = computed(() => {
        const value = route.query[AI_ASSISTANT_QUERY_PARAM];
        return Array.isArray(value) ? value[0] : value;
    });

    const conversationFromQuery = computed(() => {
        const value = queryValue.value;
        if (!value || value === '1') return null;
        return value;
    });

    const setQueryParam = (next: string | null) => {
        const current = route.query[AI_ASSISTANT_QUERY_PARAM];
        const currentValue = Array.isArray(current) ? current[0] : current;
        if (currentValue === next) return;

        const query = { ...route.query };
        if (next) {
            query[AI_ASSISTANT_QUERY_PARAM] = next;
        } else {
            delete query[AI_ASSISTANT_QUERY_PARAM];
        }
        router.replace({ query });
    };

    const onOpened = async () => {
        opened.value = true;
        const conversationId = conversationFromQuery.value;
        if (conversationId) {
            await page.init();
            await page.switchAgentConversation(conversationId);
        } else {
            await page.init();
        }
    };

    const onClosed = () => {
        opened.value = false;
        page.close();
        setQueryParam(null);
    };

    const close = () => {
        useEvent('modal:close', AI_ASSISTANT_MODAL_NAME);
    };

    // Open the modal automatically when the URL already has the assistant param
    // (deep link, page reload, or browser back/forward bringing the param back).
    onMounted(() => {
        if (queryValue.value) {
            useEvent('modal:open', AI_ASSISTANT_MODAL_NAME);
        }
    });

    // External URL changes while the modal is open should drive conversation switching.
    watch(conversationFromQuery, async (id) => {
        if (!opened.value) return;
        if (id && id !== page.currentAgentConversationId.value) {
            await page.switchAgentConversation(id);
        } else if (!id && page.currentAgentConversationId.value) {
            page.createNewAgentConversation();
        }
    });

    // When a conversation is created/switched inside the modal, reflect it in the URL.
    watch(() => page.currentAgentConversationId.value, (id) => {
        if (!opened.value) return;
        setQueryParam(id ?? '1');
    });

    // If the URL param disappears entirely (back/forward to a page without it),
    // close the modal.
    watch(queryValue, (value) => {
        if (!value && opened.value) {
            close();
        }
    });

    // Allow other parts of the app to open the modal via the standard event bus.
    useListen('modal:open', (name: string) => {
        if (name !== AI_ASSISTANT_MODAL_NAME) return;
        if (!queryValue.value) {
            setQueryParam('1');
        }
    });
</script>

<template>
    <Modal
        :name="AI_ASSISTANT_MODAL_NAME"
        modal-style="ai_overlay"
        :show-close-icon="false"
        @opened="onOpened"
        @closed="onClosed"
    >
        <button
            type="button"
            class="fixed top-[10px] right-[10px] z-[930] w-[30px] h-[30px] flex items-center justify-center rounded-md bg-white border border-black/10 shadow-[0_4px_14px_rgba(0,0,0,0.18),0_1px_3px_rgba(0,0,0,0.10)] text-[#2A2D33] hover:bg-neutral-50 hover:-translate-y-px transition cursor-pointer pointer-events-auto"
            :aria-label="$t('actions.close')"
            @click="close"
        >
            <i class="fa-regular fa-xmark text-[12px]"></i>
        </button>

        <AiPageLayout
            :preview-active="!!store.previewEntity"
            @close="close"
            @close-preview="store.closePreview"
        >
            <template #sidebar>
                <AiPageConversationSidebar
                    :agent-conversations="page.agentConversations.value"
                    :current-agent-conversation-id="page.currentAgentConversationId.value"
                    :has-more-agent-conversations="page.hasMoreAgentConversations.value"
                    :is-loading-agent-conversations="page.isLoadingAgentConversations.value"
                    @conversation-selected="page.switchAgentConversation"
                    @new-conversation="page.createNewAgentConversation"
                    @load-more="page.loadMoreAgentConversations"
                />
            </template>

            <template #chat>
                <div
                    v-if="page.isLoadingAgentConversation.value"
                    class="flex-1 flex items-center justify-center"
                >
                    <Loader
                        class-name="relative"
                        background-color="bg-transparent"
                    />
                </div>
                <AiAssistantChat v-else />
            </template>

            <template #context>
                <AiPageContextPanel
                    :entities="page.agentConversationEntities.value"
                    :preview-entity="store.previewEntity"
                    @close-preview="store.closePreview"
                />
            </template>
        </AiPageLayout>
    </Modal>
</template>
